/**
 * Ticket Template System - Group Templates
 *
 * Base templates for each of the 7 ticket type groups.
 * These define the default field configurations for each group.
 * Key-specific overrides can modify these defaults.
 */

import { TicketTypeGroup } from '../types/response'
import {
    TicketTemplate,
    TicketTemplateFields,
    DEFAULT_FIELD,
    HIDDEN_FIELD,
} from './types'
import { SAMPLE_CONTENT } from './sample-content'

/**
 * Default fields - all visible, optional
 */
const createDefaultFields = (): TicketTemplateFields => ({
    customer: { ...DEFAULT_FIELD, required: true },
    service: { ...DEFAULT_FIELD },
    content: { ...DEFAULT_FIELD, required: true },
    priority: { ...DEFAULT_FIELD },
    marjorable: { ...HIDDEN_FIELD },
    affected: { ...HIDDEN_FIELD },
    meeting: { ...HIDDEN_FIELD },
    serviceDates: { ...HIDDEN_FIELD },
    timeReply: { ...DEFAULT_FIELD },
    estimatedCompletion: { ...DEFAULT_FIELD },
    assignees: { ...DEFAULT_FIELD },
})

/**
 * Error Service Group Template
 * - For incident/error reports
 * - Marjorable and affected fields conditionally visible
 */
const ERROR_SERVICE_TEMPLATE: TicketTemplate = {
    group: 'error_service',
    fields: {
        ...createDefaultFields(),
        priority: {
            ...DEFAULT_FIELD,
            required: true,
            defaultValue: 'high',
        },
        marjorable: {
            ...HIDDEN_FIELD,
            // Shown when: key === 'error_service_incident' && priority === 'high'
            // Handled by key override
        },
        affected: {
            ...HIDDEN_FIELD,
            // Shown when: key === 'error_service_incident' && priority === 'high'
            // Handled by key override
        },
    },
    sampleContent: SAMPLE_CONTENT['group:error_service'],
    defaults: {
        ticket_priority: 'high',
    },
    validations: {},
}

/**
 * Service Request Group Template
 * - For requesting changes to existing services
 */
const SERVICE_REQUEST_TEMPLATE: TicketTemplate = {
    group: 'service_request',
    fields: {
        ...createDefaultFields(),
        priority: {
            ...DEFAULT_FIELD,
            defaultValue: 'medium',
        },
    },
    sampleContent: SAMPLE_CONTENT['group:service_request'],
    defaults: {
        ticket_priority: 'medium',
    },
    validations: {},
}

/**
 * Support Request Group Template
 * - For general support/help requests
 * - Simpler form, fewer required fields
 */
const SUPPORT_REQUEST_TEMPLATE: TicketTemplate = {
    group: 'support_request',
    fields: {
        ...createDefaultFields(),
        service: { ...DEFAULT_FIELD, required: false },
        priority: {
            ...DEFAULT_FIELD,
            defaultValue: 'low',
        },
    },
    sampleContent: SAMPLE_CONTENT['group:support_request'],
    defaults: {
        ticket_priority: 'low',
    },
    validations: {},
}

/**
 * Service Group Template
 * - For service lifecycle: create, cancel, suspend
 * - Shows service date fields
 * - Content field is optional
 */
const SERVICE_TEMPLATE: TicketTemplate = {
    group: 'service',
    fields: {
        ...createDefaultFields(),
        service: { ...DEFAULT_FIELD, required: true },
        content: { ...DEFAULT_FIELD, required: false },
        serviceDates: {
            ...DEFAULT_FIELD,
            visible: true,
            variant: 'default',
        },
    },
    sampleContent: SAMPLE_CONTENT['group:service'],
    defaults: {},
    validations: {},
}

/**
 * Meeting Group Template
 * - For scheduling meetings
 * - Shows meeting-specific fields
 * - Hides priority and time reply
 */
const MEETING_TEMPLATE: TicketTemplate = {
    group: 'meeting',
    fields: {
        ...createDefaultFields(),
        service: { ...DEFAULT_FIELD, required: false },
        content: { ...HIDDEN_FIELD },
        priority: { ...HIDDEN_FIELD },
        meeting: { ...DEFAULT_FIELD, visible: true },
        timeReply: { ...DEFAULT_FIELD, disabled: true },
        estimatedCompletion: { ...HIDDEN_FIELD },
    },
    sampleContent: SAMPLE_CONTENT['group:meeting'],
    defaults: {
        ticket_meeting_type: 'online',
        ticket_meeting_channel: 'zoom',
        ticket_meeting_purpose: 'support',
    },
    validations: {
        ticket_meeting_purpose: { required: true },
        ticket_meeting_via: { required: true },
    },
}

/**
 * Change Group Template
 * - For change requests/RFC
 * - Similar to service_request but for infrastructure changes
 */
const CHANGE_TEMPLATE: TicketTemplate = {
    group: 'change',
    fields: {
        ...createDefaultFields(),
        priority: {
            ...DEFAULT_FIELD,
            defaultValue: 'medium',
        },
    },
    sampleContent: SAMPLE_CONTENT['group:change'],
    defaults: {
        ticket_priority: 'medium',
    },
    validations: {},
}

/**
 * Internal Group Template
 * - For internal tickets
 * - Customer and service fields hidden/optional
 */
const INTERNAL_TEMPLATE: TicketTemplate = {
    group: 'internal',
    fields: {
        ...createDefaultFields(),
        customer: { ...HIDDEN_FIELD, required: false },
        service: { ...HIDDEN_FIELD, required: false },
    },
    sampleContent: SAMPLE_CONTENT['group:internal'],
    defaults: {},
    validations: {},
}

/**
 * Registry of all group templates
 */
export const GROUP_TEMPLATES: Record<TicketTypeGroup, TicketTemplate> = {
    error_service: ERROR_SERVICE_TEMPLATE,
    service_request: SERVICE_REQUEST_TEMPLATE,
    support_request: SUPPORT_REQUEST_TEMPLATE,
    service: SERVICE_TEMPLATE,
    meeting: MEETING_TEMPLATE,
    change: CHANGE_TEMPLATE,
    internal: INTERNAL_TEMPLATE,
}

/**
 * Get base template for a ticket type group
 */
export function getGroupTemplate(
    group: TicketTypeGroup | string
): TicketTemplate | undefined {
    return GROUP_TEMPLATES[group as TicketTypeGroup]
}
